import React, { useRef, useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Collapse,
  notification,
  Image,
  Switch,
  Select,
} from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Wizard } from "../types";
import { getPublicWizardUrl, getImageUrl } from "../utils/url";
import { mockWizardContent } from "../api/mockData";
import ImageUploader, {
  ImageUploaderMethods,
} from "../../../Components/ImageUploader";
import {
  uploadFile,
  dataUrlToArrayBuffer,
} from "../../../services/uploadService";
import { fetchIndustries } from "../../../services/industryService";

const { Panel } = Collapse;
const { TextArea } = Input;

const ImagePreview = ({ fieldName }: { fieldName: string }) => {
  const value = Form.useWatch(fieldName);

  // Don't show preview if no value or if it's not a string
  if (!value || typeof value !== "string") {
    return null;
  }

  // Don't show preview for values that are just labels or very short strings
  if (value.length < 10 && !value.startsWith("data:image")) {
    return null;
  }

  return (
    <Image
      src={getImageUrl(value)}
      alt="preview"
      style={{
        width: "100px",
        height: "100px",
        objectFit: "contain",
        marginBottom: "10px",
      }}
      fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkeT0iLjNlbSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTJweCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg=="
      onError={(e) => {
        console.error("Image failed to load:", getImageUrl(value));
        console.error("Original value:", value);
      }}
    />
  );
};

interface WizardFormProps {
  wizard: Wizard;
  onFinish: (wizard: Wizard) => void;
  isLoading?: boolean;
  isCreating?: boolean;
  onValuesChange?: () => void;
  isSaveDisabled?: boolean;
}

const WizardForm: React.FC<WizardFormProps> = ({
  wizard,
  onFinish,
  isLoading,
  isCreating,
  onValuesChange,
  isSaveDisabled,
}) => {
  const [form] = Form.useForm();
  const imageUploaderRef = useRef<ImageUploaderMethods>(null);
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [processingField, setProcessingField] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [industries, setIndustries] = useState<any[]>([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);

  // Watch for changes in IsSelectIndustryEnabled
  const isSelectIndustryEnabled = Form.useWatch(
    "features---IsSelectIndustryEnabled",
    form,
  );

  // Fetch industries data when component mounts
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setLoadingIndustries(true);
        const data = await fetchIndustries();
        setIndustries(data || []);
      } catch (error) {
        console.error("Failed to fetch industries:", error);
        notification.error({
          message: "Failed to load industries",
          description: "Could not load the industries list. Please try again.",
        });
      } finally {
        setLoadingIndustries(false);
      }
    };

    loadIndustries();
  }, []);

  const handleFinish = (formValues: {
    [key: string]: string | boolean | number;
  }) => {
    const updatedWizard: Wizard = {
      wizardId: formValues.wizardId as string,
      name: formValues.name as string,
      modifiedAt: new Date().toISOString(),
      content: Object.keys(formValues)
        .filter((key) => key !== "name" && key !== "wizardId")
        .reduce(
          (acc, key) => {
            const [sectionKey, fieldKey] = key.split("---");
            if (sectionKey && fieldKey) {
              if (!acc[sectionKey]) {
                acc[sectionKey] = {};
              }
              acc[sectionKey]![fieldKey] = formValues[key]!;
            }
            return acc;
          },
          {} as Wizard["content"],
        ),
    };
    onFinish(updatedWizard);
  };

  // Build a master content object that contains ALL possible fields.
  const contentWithDefaults: Wizard["content"] = Object.entries(
    mockWizardContent,
  ).reduce(
    (acc, [sectionKey, sectionValue]) => {
      acc[sectionKey] = {
        // default fields
        ...sectionValue,
        // existing wizard overrides
        ...(wizard.content[sectionKey as keyof Wizard["content"]] || {}),
      };
      return acc;
    },
    {} as Wizard["content"],
  );

  // Ensure features section has new fields
  contentWithDefaults.features = {
    autoVoucherIsOneTimeUse: false,
    IsSelectIndustryEnabled: true,
    Industry: 2, // Default to "Food & Beverages" ID
    ...(contentWithDefaults.features || {}),
  };

  // Build initial form values including defaults
  const initialValues = Object.entries(contentWithDefaults).reduce(
    (acc, [sectionKey, sectionValue]) => {
      Object.entries(sectionValue).forEach(([fieldKey, fieldValue]) => {
        acc[`${sectionKey}---${fieldKey}`] = fieldValue;
      });
      return acc;
    },
    {} as { [key: string]: string | boolean | number },
  );

  initialValues.name = wizard.name;
  initialValues.wizardId = wizard.wizardId;

  // Custom sorting to bring 'global' to the top
  const sortedContent = Object.entries(contentWithDefaults).sort((a, b) => {
    if (a[0] === "global") return -1;
    if (b[0] === "global") return 1;
    if (a[0] === "features") return -1;
    if (b[0] === "features") return 1;
    return a[0].localeCompare(b[0]);
  });

  const renderInput = (
    key: string,
    value: string | boolean | number,
    fieldName: string,
  ) => {
    if (typeof value === "boolean") {
      return <Switch defaultChecked={value} />;
    }

    // Handle Industry field - only show when IsSelectIndustryEnabled is false
    if (key === "Industry") {
      if (isSelectIndustryEnabled) {
        return null; // Don't render when industry selection is enabled
      }

      const industryOptions = industries.map((industry) => ({
        label: industry.description,
        value: industry.id,
      }));

      return (
        <Select
          placeholder="Select an industry"
          loading={loadingIndustries}
          options={industryOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    }
    if (
      (key.includes("image") || key.includes("logo") || key.includes("icon")) &&
      !key.includes("label_logo")
    ) {
      return (
        <div>
          <ImagePreview fieldName={fieldName} />
          <Button
            onClick={() => {
              if (key.includes("hero_image")) {
                setAspectRatio(720 / 900);
              } else if (key.includes("logo_main")) {
                setAspectRatio(807 / 219);
              } else {
                setAspectRatio(1);
              }
              setUploadingField(fieldName);
              imageUploaderRef.current?.openFileUpload();
            }}
            loading={processingField === fieldName}
          >
            Upload Image
          </Button>
        </div>
      );
    }
    if (key.includes("description") || key.includes("notes")) {
      return <TextArea rows={3} />;
    }
    if (key.includes("checkinValue") && key.includes("input_")) {
      return <Input placeholder="Enter check-in value" />;
    }
    // Add more conditions for other input types like Upload, Select, etc.
    return <Input />;
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
    >
      <Form.Item
        name="name"
        label="Wizard Name"
        rules={[{ required: true, message: "Please enter the wizard name!" }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="wizardId"
        label="Wizard Path (ID)"
        rules={[{ required: true, message: "Please enter a unique path!" }]}
        help="This is the unique identifier used in the URL (e.g., /wizard/checkin-for-bars)."
      >
        <Input disabled={!isCreating} />
      </Form.Item>

      <Collapse defaultActiveKey={["global", "features"]} accordion>
        {sortedContent.map(([sectionKey, sectionValue]) => (
          <Panel
            header={
              <span style={{ textTransform: "capitalize" }}>
                {sectionKey.replace(/_/g, " ").replace("step", "Step ")}
              </span>
            }
            key={sectionKey}
            forceRender
          >
            {Object.entries(sectionValue).map(([fieldKey, fieldValue]) => {
              const fieldName = `${sectionKey}---${fieldKey}`;

              // Hide Industry field when IsSelectIndustryEnabled is true
              if (fieldKey === "Industry" && isSelectIndustryEnabled) {
                return null;
              }

              return (
                <Form.Item
                  key={fieldName}
                  name={fieldName}
                  label={
                    <span style={{ textTransform: "capitalize" }}>
                      {fieldKey.replace(/_/g, " ")}
                    </span>
                  }
                  help={
                    fieldKey === "terms_link"
                      ? "Enter the full URL, e.g., https://example.com/terms"
                      : fieldKey === "IsSelectIndustryEnabled"
                        ? "Enable to show industry dropdown to end users. Disable to preset an industry for this wizard."
                        : fieldKey === "Industry"
                          ? "Select which industry this wizard is preset for when industry selection is disabled."
                          : undefined
                  }
                >
                  {renderInput(fieldKey, fieldValue, fieldName)}
                </Form.Item>
              );
            })}
          </Panel>
        ))}
      </Collapse>

      <Form.Item style={{ marginTop: "24px" }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          disabled={isSaveDisabled}
        >
          {isCreating ? "Create Wizard" : "Save Changes"}
        </Button>
      </Form.Item>
      <ImageUploader
        ref={imageUploaderRef}
        onImageResized={async ({ src }) => {
          if (uploadingField) {
            try {
              setProcessingField(uploadingField);
              const buffer = dataUrlToArrayBuffer(src);
              const filename = await uploadFile(buffer);
              console.log("Image uploaded successfully, filename:", filename);
              form.setFieldsValue({ [uploadingField]: filename });
              console.log("Form field set:", uploadingField, "=", filename);
            } catch (error) {
              console.error("Upload error:", error);
              notification.error({
                message: "Upload Failed",
                description:
                  "There was an error uploading the image. Please try again.",
              });
            } finally {
              setProcessingField(null);
              setUploadingField(null);
            }
          }
        }}
        aspectRatio={aspectRatio}
      />
    </Form>
  );
};

export default WizardForm;
